import { ICommonSettings, IJiraMcpSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigJiraMcpSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IJiraMcpSetting;
}

export interface IConfigJiraMcpSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingJiraMcpId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigJiraMcpSettings extends React.PureComponent<
  IConfigJiraMcpSettingsProps,
  IConfigJiraMcpSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigJiraMcpSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigJiraMcpSettingsProps & IWithSDProps) {
    if (prevProps.editingJiraMcpId !== this.props.editingJiraMcpId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingJiraMcpId) return;

    const editingConfig = this.props.commonSettings.jiraMcpSettings?.find((x) => x.id === this.props.editingJiraMcpId);

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const jiraMcpSetting = this.props.commonSettings?.jiraMcpSettings;
    if (!jiraMcpSetting) return;

    const isSettingAlreadyExist = jiraMcpSetting.some((x) => x.name === value && x.id !== this.props.editingJiraMcpId);

    if (isSettingAlreadyExist) throw new Error("Jira Mcp Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IJiraMcpSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      email: data.email,
      apiToken: data.apiToken,
      host: data.host?.trim()
    };

    if (this.props.editingJiraMcpId) {
      obj.id = this.props.editingJiraMcpId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "jiraMcpSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "jiraMcpSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingJiraMcpId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingJiraMcpId || "",
      configKey: "jiraMcpSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Jira Mcp setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Jira Mcp setting"
        secondHeader="Jira Mcp setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingJiraMcpId ? this.deleteSetting : undefined}
            deleteTestId="mcpJiraSettingDeleteButton"
            submitTestId="mcpJiraSettingCreateButton"
            submitLabel={this.props.editingJiraMcpId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configJiraForm"
          />
        }
      >
        <Form
          id="configJiraForm"
          name="slackSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("mcp_jiramcp_description")}>
            <ConfigField label="Name" required tooltipContentKey="mcp_jiramcp_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Jira Mcp Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Jira Mcp Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="mcpJiraSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Email" required tooltipContentKey="mcp_jiramcp_email">
              <Form.Item name="email" rules={[{ required: true, message: "Please enter a Jira Mcp Email." }]}>
                <KuikaInput size="md" data-testid="mcpJiraSettingEmailInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="API Token" required tooltipContentKey="mcp_jiramcp_apitoken">
              <Form.Item name="apiToken" rules={[{ required: true, message: "Please enter a Jira Mcp API Token." }]}>
                <KuikaInput size="md" data-testid="mcpJiraSettingApiTokenInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Host" required tooltipContentKey="mcp_jiramcp_host">
              <Form.Item name="host" rules={[{ required: true, message: "Please enter a Jira Mcp Host." }]}>
                <KuikaInput size="md" data-testid="mcpJiraSettingHostInput" autoFocus />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
